import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Fragment, useMemo, useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import Button from '../../../components/Button';
import Stepper from '../../../components/Stepper';
import SavingEnrollmentCompletedPage from './SavingEnrollmentCompletedPage';
import SavingsContribution from './SavingEnrollmentStep1';
import SavingsAutoIncrease from './SavingEnrollmentStep2';
import SavingsInvestment from './SavingEnrollmentStep3';
import SavingsReviewAndConfirmationStep from './SavingEnrollmentStep4';
import { stepScrollToTop } from '../../../utils/utils';

const savingsContributionStepData = {
  beforeTax: undefined,
  roth401: undefined,
  afterTax: undefined,
  beforeTaxCatchUp: undefined,
  roth401CatchUp: undefined,
};

const savingsAutoIncreaseStepData = {
  beforeTax: {
    savingsGoal: undefined,
    increaseBy: undefined,
    title: 'Before-Tax',
  },
  roth401: {
    savingsGoal: undefined,
    increaseBy: undefined,
    title: 'Roth 401(k)',
  },
  afterTax: {
    savingsGoal: undefined,
    increaseBy: undefined,
    title: 'After-Tax',
  },
  beforeTaxCatchUp: {
    savingsGoal: undefined,
    increaseBy: undefined,
    title: 'Before-Tax Catch Up',
  },
  roth401CatchUp: {
    savingsGoal: undefined,
    increaseBy: undefined,
    title: 'Roth 401(k) Catch Up',
  },
};

const savingsInvestment = {
  investmentOption: undefined,
  quickInvest: {
    quickInvestOption: undefined,
  },
  investMyself: {
    targetDateFunds: undefined,
    domestic: undefined,
    international: undefined,
    bondIncome: undefined,
    clientSpecific: undefined,
  },
};

const savingsReviewAndConfirmationData = {
  confirmation: undefined,
};

export type SavingsEnrollmentStepperProps = {
  isVisible: (isVisible: boolean) => void;
};

const SavingsEnrollmentStepper = ({
  isVisible,
}: SavingsEnrollmentStepperProps) => {
  const [step, setStep] = useState(0);
  const [step1, setStep1] = useState(savingsContributionStepData);
  const [step2, setStep2] = useState(savingsAutoIncreaseStepData);
  const [step3, setStep3] = useState(savingsInvestment);
  const [step4, setStep4] = useState(savingsReviewAndConfirmationData);

  const isStep1Valid = useMemo(() => {
    const { beforeTax, roth401, afterTax, beforeTaxCatchUp, roth401CatchUp } =
      step1;

    let totalSum = 0;

    if (beforeTax) {
      totalSum += beforeTax;
    }
    if (roth401) {
      totalSum += roth401;
    }
    if (afterTax) {
      totalSum += afterTax;
    }
    if (beforeTaxCatchUp) {
      totalSum += beforeTaxCatchUp;
    }
    if (roth401CatchUp) {
      totalSum += roth401CatchUp;
    }

    if (totalSum < 6) {
      return false;
    }

    return true;
  }, [step1]);

  const handleStep1Data = (data: any) => {
    setStep1(data);
  };

  const handleStep2Data = (data: any) => {
    setStep2(data);
  };

  const handleStep3Data = (data: any) => {
    setStep3(data);
  };

  const handleStep4Data = (data: any) => {
    setStep4(data);
  };

  const handleEditPreviousStep = (step: number) => {
    setStep(step);
  };

  const steps = [
    {
      title: 'Savings Contribution',
      child: (
        <SavingsContribution data={step1} collectStepData={handleStep1Data} />
      ),
    },
    {
      title: 'Auto Increase',
      child: (
        <SavingsAutoIncrease
          data={step2}
          step1Data={step1}
          collectStepData={handleStep2Data}
        />
      ),
    },
    {
      title: 'Investment',
      child: (
        <SavingsInvestment data={step3} collectStepData={handleStep3Data} />
      ),
    },
    {
      title: 'Confirmation',
      child: (
        <SavingsReviewAndConfirmationStep
          data={step4}
          dataSavingsContribution={step1}
          dataAutoIncrease={undefined}
          dataInvestment={undefined}
          collectStepData={handleStep4Data}
          editPreviousStep={handleEditPreviousStep}
        />
      ),
    },
  ];

  const handleStepForward = () => {
    setStep(step + 1);
  };

  const handleStepBackward = () => {
    setStep(step - 1);
  };

  const handleClose = () => {
    isVisible(false);
  };

  const isStepValid = () => {
    if (step === 0 && !isStep1Valid) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    stepScrollToTop();
  }, [step]);

  return (
    <Fragment>
      {step === steps.length ? (
        <SavingEnrollmentCompletedPage />
      ) : (
        <>
          <Container className="mb-12 mx-0 px-0">
            <Stepper currentStepIndex={step} steps={steps} />
          </Container>
          <section className="d-flex flex-column flex-lg-row justify-content-center">
            <Button
              variant="primary-outline"
              leftIcon={light('arrow-left')}
              onClick={handleStepBackward}
              disabled={step === 0}
              className={`mx-lg-10 ${
                step === 0 && 'd-none'
              } order-2 order-lg-1 mt-7 mt-lg-0`}
            >
              Previous
            </Button>
            <Button
              variant="primary-outline"
              onClick={handleClose}
              className={'mx-lg-10 order-3 order-lg-2 mt-7 mt-lg-0'}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              // disabled={isStepValid()}
              onClick={handleStepForward}
              className={'mx-lg-10 order-1 order-lg-3'}
            >
              {step === steps.length - 1 ? 'Complete' : 'Continue'}
            </Button>
          </section>
        </>
      )}
    </Fragment>
  );
};

export default SavingsEnrollmentStepper;
